/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import _ from 'lodash';
import { useMutation } from 'react-query';
import Select from 'react-select';
import { OrderCardContext } from '../../../../../contexts/card/cardOrder/context';
import {
  CARD_NETWORKS,
  cardProviders,
  mainCardTypes,
  orderCardFlowScreens,
  TermsSettingsUrls,
} from '../../../constants';
import { CARD_ORDER_PROMO, CARD_ORDER_WAITING_LIST } from '../../../mutations';
import { showAlert } from '../../../../../utilities/notifications';
import { MONAVATE_CARD_STATUS } from '../../../queries';
import getQuery from '../../../../../utilities/query';
import { FLOWS, sendStats } from '../../../../../services/mixpanel';
import {
  MXP_CARD_ORDER_CONFIRM_DETAILS,
  MXP_CARD_ORDER_CONFIRM_DETAILS_VISIT,
} from '../../mixpanel-events';
import ModalBodyLayout from '../partials/MainBodyLayout';
import { Button, Typography } from '../../../../../components';
import SupportModal from '../../../../Accounts/modals/SupportModal';
import { formatMonovateCardHolderName } from '../../../../../utilities/sharedHooks/user';
import { CustomSelectOption, CustomSelectValue } from '../../../../../components/shared';
import MastercardLogo from '../../../../../assets/images/mastercard.svg';
import VisaLogo from '../../../../../assets/images/visa.svg';
import TermsCheckbox from '../../../../../components/TermsCheckbox';
import { cardSettingsScreens } from '../../../Settings/constants';
import VisaUSLogo from '../../../../../assets/images/visa-logo.svg';

const ConfirmDetails = () => {
  const { t: translate } = useTranslation();
  const history = useHistory();
  const queryClient = getQuery();
  const { path } = useRouteMatch();

  const { state, openScreen, updateCardCurrency } = useContext(OrderCardContext);

  const { isEligible, isWaitingList } = state?.freeCardEligible;
  const userRegionSettings = state?.regionSettings;
  const user = state?.user;
  const defaultCardCurrency = state?.cardCurrency?.coin;
  const mainCardType = state?.mainCardType;
  const mailingAddresses = state?.accountMailingAddress;
  const cardType = state?.cardType;
  const cardCurrency = state?.cardCurrency;
  const cardOptions = state?.cardOptions;
  const userFirstName = state?.cardHolderName?.firstName;
  const userLastName = state?.cardHolderName?.lastName;
  const isCardReplacement = state?.isCardReplacement;
  const cardNetwork = state?.cardNetwork;

  // Settings
  const ledgerSupportEmailAddress = state?.cardOrderSettings?.server?.support_email_address;
  const uiFeatureShowHint = state?.cardOrderSettings?.ui?.ui_feature_show_hints;
  const agreementUrl = state?.cardOrderSettings?.contentUrls?.cardholder_term_galileo_us_user;

  const [selectedTrouble, setSelectedTrouble] = useState(false);
  const [termAndConditionChecked, setTermAndConditionChecked] = useState(false);

  const deliveryAddress = useMemo(() => {
    let address = '';
    if (user) {
      if (mailingAddresses && mailingAddresses.length > 0) {
        // we should use mailing address as delivery address.
        const mailingAddress = mailingAddresses[0];
        address = `${mailingAddress.address_line_1}, ${mailingAddress.address_line_2}, ${mailingAddress.city}, ${mailingAddress.state}, ${mailingAddress.post_code}, ${user.countryDisplayName}`;
      } else {
        // use the default physical address.
        address = `${user.street}, ${_.isEmpty(user?.houseNumber) ? `` : `${user?.houseNumber},`} ${
          user.city
        }, ${user.postCode}, ${user.countryDisplayName}`;
      }
    }

    return address;
  }, [user, mailingAddresses]);

  const availableToOrderCurrencies = useMemo(() => {
    if (defaultCardCurrency) {
      const result = [
        {
          coin: defaultCardCurrency.toUpperCase(),
        },
      ];
      return result;
    }
    return [];
  }, [defaultCardCurrency]);

  const handleClick = () => {
    if (uiFeatureShowHint) {
      history.push('/hint/card');
    }
  };

  useEffect(() => {
    sendStats(
      MXP_CARD_ORDER_CONFIRM_DETAILS_VISIT,
      {
        mainCardType,
        cardProvider: cardType,
        cardCurrency: cardCurrency?.coin?.toUpperCase(),
        isEligibleForFreeCard: isEligible,
        isWaitingListTopRated: isWaitingList,
      },
      FLOWS.CARD_ORDER
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const CardInfo = useMemo(
    () => ({
      title:
        cardType === cardProviders.MONAVATE ||
        (cardType === cardProviders.GALILEO && cardNetwork === CARD_NETWORKS.MASTERCARD)
          ? translate('card_order_confirm_mastercard_title')
          : translate('card_order_confirm_visacard_title'),
      type: translate('card_type_label').replace(
        '__TYPE__',
        mainCardTypes.PHYSICAL.charAt(0).toUpperCase() + mainCardTypes.PHYSICAL.slice(1)
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cardType]
  );

  const cardTermsUrl = useMemo(() => {
    if (cardType === cardProviders.MONAVATE) {
      return TermsSettingsUrls.CARD_MONAVATE_TERMS_CONTENT_URL;
    }
    return TermsSettingsUrls.CARD_GALILEO_TERMS_CONTENT_URL;
  }, [cardType]);

  const onOrderCardSuccessfully = () => {
    history.push(`${path}details`);
  };

  const { mutate: orderCardPromo, isLoading: isLoadingOrderPromo } = useMutation(CARD_ORDER_PROMO, {
    onSettled: async (data, error) => {
      if (error) {
        showAlert({ message: error.message, type: 'danger' });
      } else if (data && data.success) {
        let successMessage = translate('card_request_success');
        if (cardType === cardProviders.MONAVATE) {
          successMessage = translate('mastercard_monavate_request_success');
          await queryClient.invalidateQueries(MONAVATE_CARD_STATUS);
        }
        showAlert({
          message: successMessage,
          type: 'success',
          showIcon: !userRegionSettings?.allow_manual_card_order,
          blockAutoDismiss: !userRegionSettings?.allow_manual_card_order,
          onClick: userRegionSettings?.allow_manual_card_order ? () => {} : handleClick,
        });
        onOrderCardSuccessfully();
      } else {
        showAlert({
          message: translate('card_order_error_msg').replace(
            '__SUPPORT_EMAIL__',
            ledgerSupportEmailAddress
          ),
          type: 'danger',
        });
      }
    },
  });

  const { mutate: orderWaitingList, isLoading: isLoadingOrderWaitingList } = useMutation(
    CARD_ORDER_WAITING_LIST,
    {
      onSettled: async (data, error) => {
        if (error) {
          showAlert({ message: error.message, type: 'danger' });
        } else if (data && data.success) {
          let successMessage = translate('card_request_success');
          if (cardType === cardProviders.MONAVATE) {
            successMessage = translate('mastercard_monavate_request_success');
            await queryClient.invalidateQueries(MONAVATE_CARD_STATUS);
          }
          showAlert({
            message: successMessage,
            type: 'success',
            showIcon: !userRegionSettings?.allow_manual_card_order,
            blockAutoDismiss: !userRegionSettings?.allow_manual_card_order,
            onClick: userRegionSettings?.allow_manual_card_order ? () => {} : handleClick,
          });
          onOrderCardSuccessfully();
        } else {
          showAlert({
            message: translate('card_order_error_msg').replace(
              '__SUPPORT_EMAIL__',
              ledgerSupportEmailAddress
            ),
            type: 'danger',
          });
        }
      },
    }
  );

  const dropdownIndicatorStyles = (base) => {
    const changes = {
      display: 'none',
    };
    return Object.assign(base, changes);
  };

  const renderFooter = () => (
    <div className="btn-container">
      <Button
        variant="primary"
        className="w-100"
        onClick={() => {
          sendStats(
            MXP_CARD_ORDER_CONFIRM_DETAILS,
            {
              mainCardType,
              cardProvider: cardType,
              cardCurrency: defaultCardCurrency?.toUpperCase(),
              isEligibleForFreeCard: isEligible,
              isWaitingListTopRated: isWaitingList,
            },
            FLOWS.CARD_ORDER
          );
          if (mainCardType === mainCardTypes.PHYSICAL) {
            if (isEligible && !isWaitingList) {
              if (isCardReplacement) {
                openScreen(orderCardFlowScreens.SELECT_PAYMENT_TYPE);
              } else {
                orderCardPromo({
                  cardProvider: cardType,
                  platform: defaultCardCurrency?.toUpperCase(),
                });
              }
            } else if (isWaitingList) {
              orderWaitingList({
                cardProvider: cardType,
                platform: defaultCardCurrency?.toUpperCase(),
              });
            } else {
              openScreen(orderCardFlowScreens.SELECT_PAYMENT_TYPE);
            }
          } else {
            openScreen(orderCardFlowScreens.SELECT_PAYMENT_TYPE);
          }
        }}
        loading={isLoadingOrderPromo || isLoadingOrderWaitingList ? 'true' : 'false'}
        disabled={!termAndConditionChecked || isLoadingOrderPromo || isLoadingOrderWaitingList}>
        {translate('continue_btn_title')}
      </Button>
    </div>
  );

  const cardLogo = useMemo(() => {
    if (cardType === cardProviders.GALILEO && cardNetwork === CARD_NETWORKS.VISA) {
      return VisaUSLogo;
    }
    if (cardNetwork === CARD_NETWORKS.VISA) {
      return VisaLogo;
    }
    return MastercardLogo;
  }, [cardType, cardNetwork]);

  return (
    <ModalBodyLayout
      withBack
      onPrevClick={() => {
        if (isCardReplacement) {
          history.push('/dashboard/card/settings', {
            initScreen: cardSettingsScreens.REPORT_CARD_CONFIRM,
          });
        } else if (
          mainCardType !== mainCardTypes.VIRTUAL &&
          cardType in cardOptions &&
          cardOptions[cardType]?.length >= 2
        ) {
          openScreen(orderCardFlowScreens.SELECT_CARD_TYPE);
        } else {
          openScreen(orderCardFlowScreens.FREE_CARD_PROMO);
        }
      }}
      title={translate('card_order_confirm_details_title')}>
      <div className="mt-4 pt-3">
        <div>
          <Typography variant="base" className="text-dark">
            {translate('card_order_confirm_details_subtitle')}
          </Typography>
          <div
            onClick={() => setSelectedTrouble(true)}
            aria-hidden="true"
            className="mt-4 mb-2 cursor-pointer">
            <Typography as="b" variant="base" className="text-primary p-0 text-btn-item">
              {translate('card_order_confirm_name_having_trouble')}
            </Typography>
          </div>
          <div className="bg-lighter rounded-3 p-3">
            {cardType === cardProviders.MONAVATE ? (
              <div>
                <div className="form-group bg-lighter p-0 my-3 mt-0">
                  <label className="text-f6 text-shadow">
                    {translate('card_order_confirm_details_cardholder_name')}
                  </label>
                  <input
                    readOnly
                    value={formatMonovateCardHolderName(userFirstName, userLastName)}
                    type="text"
                    className="form-control bg-lighter px-0 border-0 border-shadow rounded-0 shadow-none"
                  />
                </div>
              </div>
            ) : null}
            <div>
              <div className="form-group bg-lighter p-0 my-3 mt-0">
                <label className="text-f6 text-shadow">
                  {translate('card_order_confirm_details_full_name')}
                </label>
                <input
                  readOnly
                  value={`${userFirstName} ${userLastName}`}
                  type="text"
                  className="form-control bg-lighter px-0 border-0 border-shadow rounded-0 shadow-none"
                />
              </div>
            </div>
            {mainCardType === mainCardTypes.PHYSICAL && (
              <div>
                <div className="form-group bg-lighter p-0 mt-3 mb-0">
                  <label className="text-f6 text-shadow">
                    {translate('card_order_confirm_details_address')}
                  </label>
                  <textarea
                    readOnly
                    rows={2}
                    value={deliveryAddress}
                    style={{ resize: 'none' }}
                    className="form-control bg-lighter px-0 border-0 border-shadow rounded-0 shadow-none position-relative"
                  />
                </div>
              </div>
            )}

            <div className="pe-0">
              <div className="select-group bg-lighter px-0 border-0 border-shadow rounded-0 shadow-none mt-0 mb-3">
                <label className="text-f6 text-shadow">
                  {translate('card_order_confirm_details_card_currency')}
                </label>
                <Select
                  defaultValue={cardCurrency}
                  value={cardCurrency}
                  className="react-select-container no-borders transparent my-0"
                  classNamePrefix="react-select"
                  options={availableToOrderCurrencies}
                  components={{
                    Option: CustomSelectOption,
                    SingleValue: CustomSelectValue,
                  }}
                  getOptionValue={(option) => option.coin}
                  onChange={(option) => updateCardCurrency(option)}
                  styles={{ dropdownIndicator: dropdownIndicatorStyles }}
                  isDisabled
                />
              </div>
            </div>
            <div>
              <label className="text-f6 text-shadow">
                {translate('card_order_confirm_details_card_type')}
              </label>
              <div className="d-flex flex-row justify-content-between align-items-center">
                <div className="text-dark text-f5">
                  {CardInfo.title}{' '}
                  {mainCardType === mainCardTypes.PHYSICAL
                    ? 'Physical + Virtual card'
                    : CardInfo.type}
                </div>
                <div style={{ width: '2rem' }} className="d-flex justify-content-end">
                  <img src={cardLogo} alt="Mastercard" className="mx-2" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-3">
          <TermsCheckbox
            contentUrlKey={cardTermsUrl}
            checked={termAndConditionChecked}
            onChange={() => setTermAndConditionChecked((prev) => !prev)}>
            {user?.isUS ? (
              <>
                {translate('before_cardholder_agreement_checkbox_link_label')}{' '}
                <a
                  target="_blank"
                  className="link text-decoration-none"
                  href={agreementUrl || '#'}
                  download={translate('cryptodraft_cardholder_agreement_title')}
                  rel="noreferrer">
                  {translate('cardholder_agreement_checkbox_link_label')}
                </a>{' '}
              </>
            ) : null}
          </TermsCheckbox>
        </div>
      </div>
      {selectedTrouble && <SupportModal hide={() => setSelectedTrouble(false)} />}
      {renderFooter()}
    </ModalBodyLayout>
  );
};

export default ConfirmDetails;
